/* eslint-disable @typescript-eslint/no-magic-numbers */

import React from 'react';
import { Typography } from '@cp/ds/src/components/typography';
import { Stack } from '@cp/ds/src/components/stack';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { Box } from '@cp/ds/src/components/box';
import { ImageAsset } from '@common/model/resource/image';
import { CareerId } from '@common/const/catalog/career';
import { buildImageSources } from '@cp/ds/src/components/image/utils';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { styled } from '@cp/ds/src/theme/styled';
import { Image as Img } from '@youhelp-ru/ui';
import { getAssetSrc, toAssetItem } from '@cp/shared/image';
import NannyImage340 from '../../../../assets/images/catalogHowToFindJobNanny340.jpg';
import NannyImageWebp340 from '../../../../assets/images/catalogHowToFindJobNanny340.jpg?format=webp';
import NannyImage500 from '../../../../assets/images/catalogHowToFindJobNanny500.jpg';
import NannyImageWebp500 from '../../../../assets/images/catalogHowToFindJobNanny500.jpg?format=webp';
import NurseImage340 from '../../../../assets/images/catalogHowToFindJobNurse340.jpg';
import NurseImageWebp340 from '../../../../assets/images/catalogHowToFindJobNurse340.jpg?format=webp';
import NurseImage500 from '../../../../assets/images/catalogHowToFindJobNurse500.jpg';
import NurseImageWebp500 from '../../../../assets/images/catalogHowToFindJobNurse500.jpg?format=webp';
import { CatalogBannerHowToFindJobProps } from './model';

const Image = styled(Img)({
  width: '100%',
});

const imageAssetItemsDict: Partial<Record<CareerId, ImageAsset>> = {
  [CareerId.Nanny]: {
    base: toAssetItem(NannyImage500, NannyImage340),
    webp: toAssetItem(NannyImageWebp500, NannyImageWebp340),
  },
  [CareerId.Nurse]: { base: toAssetItem(NurseImage340, NurseImage500), webp: toAssetItem(NurseImageWebp340, NurseImageWebp500) },
};
const IMAGE_WIDTH_DESKTOP = 240;
const IMAGE_WIDTH_MOBILE = '100%';

const ImageWrapper = styled(Box)(({ theme }) => ({
  borderRadius: '24px',
  overflow: 'hidden',
  width: IMAGE_WIDTH_MOBILE,
  minWidth: IMAGE_WIDTH_MOBILE,
  maxWidth: IMAGE_WIDTH_MOBILE,
  height: 'fit-content',
  [theme.breakpoints.up('md')]: {
    width: IMAGE_WIDTH_DESKTOP,
    minWidth: IMAGE_WIDTH_DESKTOP,
    maxWidth: IMAGE_WIDTH_DESKTOP,
  },
}));

export const CatalogBannerHowToFindJob: React.FC<CatalogBannerHowToFindJobProps> = ({ careerId, careerDict }) => {
  const isMobile = useIsMobile();
  const theme = useTheme();

  const asset = React.useMemo((): ImageAsset => {
    return (imageAssetItemsDict[careerId] ? imageAssetItemsDict[careerId] : imageAssetItemsDict[CareerId.Nanny]) as ImageAsset;
  }, [careerId]);
  const [webpSources, baseSources] = React.useMemo(() => {
    const grid = { xs: 340, md: 500 };
    return [buildImageSources(asset.webp || asset.base, grid, theme), buildImageSources(asset.base, grid, theme)];
  }, [asset, theme]);

  if (!careerDict) {
    return <ContentSkeleton blocks={1} />;
  }

  return (
    <Stack alignItems="center" sx={{ p: { xs: '8px 12px 12px', md: '12px 30px 30px' }, backgroundColor: 'white' }}>
      <Box sx={{ border: '1px solid grey', width: { xs: '30px', md: '73px' }, mb: '20px' }} />
      <Typography component="div" sx={{ textAlign: 'center', mb: isMobile ? 1.5 : 3 }} variant={isMobile ? 'h2' : 'h1'}>
        Как {careerDict[careerId]?.translation.nameSingleDative ?? 'няне'} найти работу
      </Typography>

      <Stack direction={isMobile ? 'column' : 'row'}>
        <ImageWrapper>
          <picture>
            {webpSources.map((source) => (
              <source key={source.srcSet} media={source.media} srcSet={source.srcSet} type={asset.webp?.mimeType} />
            ))}
            {baseSources.map((source) => (
              <source key={source.srcSet} media={source.media} srcSet={source.srcSet} type={asset.base.mimeType} />
            ))}
            <Image alt="" loading="lazy" src={getAssetSrc(asset)} />
          </picture>
        </ImageWrapper>

        <Box sx={{ ml: isMobile ? 0 : 3.75, mt: isMobile ? 1.5 : 0 }}>
          <Typography component="div" variant="body bold">
            Зарегистрируйтесь на сайте в качестве {careerDict[careerId]?.translation.nameSingleGenitive ?? 'специалиста'}, чтобы попасть в
            нашу базу соискателей.
            <br />
            <br />
          </Typography>

          <Typography component="div" variant="body">
            Ваши контакты будут видны тысячам нанимателей, а это значит, вас в любой момент могут пригласить на работу.
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
};
